import React, { useEffect, useRef, useState } from "react";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { fetchClubBookLatestGuest } from "../../api/clubApi";
import CustomLoadingHTML from "../../components/customLoadingHTML/customLoadingHTML";
import { unwrapClubBookLatest } from "../../helpers/clubShareQr";
import { showToast } from "../../store/features/toast/toastSlice";

/**
 * Public route: /clubs/:slug/book
 * Resolves the club's latest upcoming event and redirects guests to booking.
 */
const ClubBookLatestContainer = () => {
  const { slug: rawSlug } = useParams();
  const slug = String(rawSlug || "").trim().toLowerCase();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [error, setError] = useState("");
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (!slug || redirectedRef.current) return;
    let cancel = false;

    (async () => {
      try {
        const raw = await fetchClubBookLatestGuest(slug);
        if (cancel) return;
        const data = unwrapClubBookLatest(raw);
        if (!data) {
          throw new Error(t("CLUB_BOOK_LATEST_LOAD_ERROR"));
        }

        const redirectUrl = String(
          data.redirectUrl || data.latestEvent?.bookUrl || data.websiteUrl || ""
        ).trim();

        if (!redirectUrl) {
          throw new Error(t("CLUB_BOOK_LATEST_LOAD_ERROR"));
        }

        if (!data.hasUpcomingEvent) {
          dispatch(
            showToast({
              message: t("CLUB_BOOK_LATEST_NO_EVENTS"),
              type: "info",
            })
          );
        }

        redirectedRef.current = true;
        window.location.replace(redirectUrl);
      } catch (err) {
        if (cancel) return;
        setError(
          typeof err === "string"
            ? err
            : err?.message || t("CLUB_BOOK_LATEST_LOAD_ERROR")
        );
      }
    })();

    return () => {
      cancel = true;
    };
  }, [dispatch, slug, t]);

  const title = t("CLUB_BOOK_LATEST_PAGE_TITLE");

  if (error) {
    return (
      <div className="club-book-latest-page">
        <Helmet title={title} />
        <div className="container py-5 text-center">
          <p className="text-danger mb-0">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="club-book-latest-page">
      <Helmet title={title} />
      <CustomLoadingHTML />
    </div>
  );
};

export default ClubBookLatestContainer;
